/* ============================================
   EMAIL TEMPLATES — CONFIRMING A SAMPLE ORDER
   Asset: email-tpl-samples-confirm.js
   CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-20)

   Somebody orders free ECORE samples on the website. The rep rings them to confirm the
   address, answer questions and find out what the job actually is. They do not pick up. The
   rep leaves a voicemail, and then sends THIS.

   ⛔ THE REAL JOB OF THESE THREE EMAILS IS TO FIND OUT WHETHER THE PERSON IS REAL, AND THEY
   MUST NEVER SAY SO. Ryan: *"the goal is to just make sure they're legitimate without saying
   that or making them feel called out - because we do get some paper pushers."* So the
   mechanism is a question they can only answer by engaging: **confirm the address**. A real
   buyer answers it in four words. A tyre-kicker answers nothing, and nobody has been accused
   of anything. Every rung therefore ends on that same small ask, and none of them mentions
   verification, qualification, or the cost of shipping a box.

   ⚠️ THESE ARE NOT THE OTHER SAMPLES LADDER. The four-rung follow-up ladder lives in the
   worker (email/samples-followup.js), is sent by a cron, and starts AFTER the box is
   delivered - it asks what they thought. This family sits on the other side of the shipment
   and is sent by the REP, by hand, because only the rep knows the call went unanswered.
   📌 An earlier email-tpl-samples.js held the post-delivery copy and was deleted on
   2026-08-19 when the worker took that job over ("that copy doesn't need to live in the
   templates anymore since this is no longer a manually triggered template email"). This file
   is not that file coming back.

   ⛔ THEY STOP THE MOMENT THE BOX SHIPS. A tracking number on the deal means the address
   question is answered and the worker's ladder owns the conversation from there, so all three
   rows grey out. Chasing a confirmation for a box already in transit is the kind of email
   that makes a company look like it is not reading its own records.
   ============================================ */
(function () {
  'use strict';

  var T = window.RQDTpl;
  var B = window.RQDTplBlocks;
  if (!T || !B) { console.error('[email-tpl] email-tpl-samples-confirm.js loaded before the kernel'); return; }

  /* Mirrors the kernel's own paver test (_isPaver), which reads a TITLE when there is no
     uom. Sample selections arrive as display strings off the lead ("FREE Sample Rubber Paver
     - Paver - 1-3/4 in / Solid Black"), so the title is all there is. One rule, stated in two
     places because the shapes differ; if the kernel's regex changes, change this with it. */
  var PAVER_RX = /paver/i;

  /* ── what they ordered, in words ──────────────────────────────────────────
     ⚠️ ALL-OR-NOTHING, THE SAME RULE THE KERNEL'S categoryPhrase USES. A basket holding both
     is "flooring and paver", never one of them: naming a mixed order after half of it is the
     one sentence in the email that would be factually wrong, and it is in the first line. */
  function _kinds(titles) {
    var list = (titles || []).filter(function (s) { return s && String(s).trim(); });
    var pavers = list.filter(function (s) { return PAVER_RX.test(String(s)); }).length;
    return { total: list.length, pavers: pavers, swatches: list.length - pavers };
  }

  /* ── how the order is NAMED, in the subject and in the first line ─────────
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-13). Three baskets, three names, and the two
     with a paver in them stopped saying "ECORE" at all:

       ALL PAVERS  subject "Rubber Paver Sample Order"  body "the rubber paver sample(s)"
       MIXED       subject "Paver & Flooring Samples"   body "the rubber paver and flooring samples"
       NO PAVER    subject "ECORE Sample(s) Order"      body "the ECORE flooring sample(s)"

     ⛔ THE PAVER SUBJECT SAYS "Sample" WHETHER THEY TOOK ONE OR SIX. Ryan: *"regardless as to
     whether they order 1 or 2+ rubber pavers."* The subject names the ORDER; the first line of
     the body, one scroll down, is where the count is still honoured. So the two disagree on
     purpose - do not "fix" one to match the other.

     ⛔ THE NO-PAVER BASKET IS DELIBERATELY UNTOUCHED (Ryan 2026-09-13, asked directly before
     building). A basket with no paver in it still reads ECORE in both places. The material
     naming is therefore NOT applied uniformly here, and that is a decision rather than a
     half-finished edit.

     ⚠️ MIXED IS ALL-OR-NOTHING, the same rule the kernel's categoryPhrase uses: a basket
     holding both is named after BOTH. Naming it after half of itself is the one sentence in
     the email that would be factually wrong, and it sits in the first line. */
  function _subjectPhrase(k) {
    if (k.pavers && k.swatches) return 'Paver & Flooring Samples';
    if (k.pavers) return 'Rubber Paver Sample Order';
    return 'ECORE ' + (k.total === 1 ? 'Sample' : 'Samples') + ' Order';
  }

  /* "the rubber paver samples" / "the ECORE flooring sample" - what they ordered, definite,
     because by the time this sends it is a specific box we are both talking about. Ryan's
     second pass moved off "some ECORE flooring samples" for exactly that reason.
     ⚠️ A basket with nothing recorded on it falls to "the ECORE samples" - no category word
     invented, and the rep already has a warning saying so. */
  function _theirOrder(k) {
    if (k.pavers && k.swatches) return 'the rubber paver and flooring samples';
    var noun = k.total === 1 ? 'sample' : 'samples';
    if (k.pavers) return 'the rubber paver ' + noun;
    return 'the ECORE ' + (k.total ? 'flooring ' : '') + noun;
  }

  /* ============================================
     The subject
     ============================================
     Ryan, second pass: `[First Last] - ECORE Sample Order - [reference]`. Three parts, and
     each one is doing a job:

       • THE NAME FIRST, because a rep scanning their own Sent folder is looking for a person,
         not a category. ⛔ Run through T.properName, so a form that was filled in as
         "BRian wAlrod" does not shout it back at them in a subject line - the exact defect
         found in the vendor email on 2026-08-20.
       • WHAT THEY ORDERED, which is `_subjectPhrase` above and is the half Ryan rewrote on
         2026-09-13: a paver basket says "Rubber Paver Sample Order", a mixed one says
         "Paver & Flooring Samples", and only a basket with no paver in it still says ECORE.
       • THE REFERENCE, which is new here and is the half that pays off later: it is what
         makes a reply land against the right deal in the mailbox sync, the same way every
         other reference in a subject does.

     ⛔ ONLY RUNG 1 SETS IT. The two below reply into this thread, so the subject set here is
     the one the whole conversation keeps. */
  function _subject(r, k) {
    var who = T.properName(r.fullName);
    if (!who) r.warnings.push('No customer name is on this deal, so the subject line starts with the order rather than the person.');
    return (who ? who + ' - ' : '') + _subjectPhrase(k) + (r.ref ? ' - ' + r.ref : '');
  }

  /* ── where the box goes ───────────────────────────────────────────────────
     ⛔ THE ADDRESS IS PRINTED SO CONFIRMING IT COSTS ONE WORD. Ryan's copy shows the address
     and asks them to confirm it; asking "what is your address?" instead would make a busy
     person type it out, which is exactly the friction that loses the reply we are fishing
     for. ⚠️ And when we genuinely have no address the email must ASK rather than print an
     empty block - see _build1, which swaps the sentence and warns the rep. */
  function _pick(o) {
    if (!o || typeof o !== 'object') return null;
    var street = String(o.street || o.address1 || '').trim();
    var city = String(o.city || '').trim();
    if (!street || !city) return null;
    return {
      street: street,
      street2: String(o.street2 || o.address2 || '').trim(),
      city: city,
      state: String(o.state || o.province || o.provinceCode || '').trim(),
      zip: String(o.zip || o.postalCode || o.postal_code || '').trim(),
    };
  }

  /* One line, comma separated: "1725 Del Mar Ave, Suite 9, Laguna Beach, CA 92651". The
     pieces are assembled the same way they always were; only the join changed when the copy
     went inline. */
  function _addressLine(details, dq) {
    var q = (details && details.quote) || {};
    var wm = (details && details.workstationMeta) || {};
    /* Ship To first, everywhere, because a sample box follows the same rule the real order
       will: the place they want it delivered, not the place they are billed. */
    var a = _pick(dq && dq.shipTo) || _pick(q.shipTo) || _pick(q.shippingAddress)
      || _pick(wm.customer) || _pick(q.customer)
      || _pick(dq && { street: dq.customerStreet, street2: dq.customerStreet2, city: dq.city, state: dq.state, zip: dq.zip });
    if (!a) return '';
    var parts = [a.street];
    if (a.street2) parts.push(a.street2);
    if (a.city) parts.push(a.city);
    var region = [a.state, a.zip].filter(Boolean).join(' ');
    if (region) parts.push(region);
    return parts.join(', ');
  }

  /* ⛔ NO NAME LINE IN THAT BLOCK, DELIBERATELY. The customer knows their own name, so it
     adds nothing to what they are being asked to check - and the stored name is whatever
     they typed into the form, which is how "BRian wAlrod" reached a vendor subject line on
     2026-08-20. The worker repairs names on ITS emails; nothing here can, so the safest
     thing this email can do with a name it did not write is not print it. */

  /* ── the shared read ──────────────────────────────────────────────────────
     All three rungs need the same five facts, so the read happens once per build and the
     copy functions stay readable. Everything is fetched in parallel: the picker re-renders
     when the attachment list changes, and a serial chain would be felt every time. */
  function _read(ctx) {
    var ref = String((ctx && ctx.ref) || '').trim();
    return Promise.all([
      T.loadDetails(ref),
      T.ensureChain(ref),          // a call logged on a sibling revision still counts
      T.loadRepSettings(),
    ]).then(function (res) {
      var details = res[0] || null;
      var settings = res[2] || {};
      var dq = T.dealByRef(ref);
      var deals = T.dealSet(ref);
      var warnings = [];

      var quote = (details && details.quote) || null;
      var fullName = (quote && quote.customer && quote.customer.name) || (dq && dq.customer) || '';
      var first = T.firstName(fullName);
      if (!first) {
        /* Same last resort the inquiry follow-up uses: a name out of the address they wrote
           to. Better than "Hi there," on an email whose whole purpose is to feel personal. */
        var to = (Array.isArray(ctx && ctx.toEmails) ? ctx.toEmails : [])[0] || '';
        first = T.firstName(String(to).split('@')[0].replace(/[._-]+/g, ' '));
      }

      var titles = (dq && Array.isArray(dq.leadSamples)) ? dq.leadSamples : [];
      var kinds = _kinds(titles);
      if (!kinds.total) {
        warnings.push('No sample selections are recorded on this deal, so the email says "samples" without naming what kind.');
      }

      return {
        ref: ref, dq: dq, deals: deals, details: details, warnings: warnings,
        first: first, fullName: fullName, kinds: kinds,
        address: _addressLine(details, dq),
        phone: String(settings.repPhone || '').trim(),
        parent: T.lastOutboundEmail(deals),
        now: T.now(),
      };
    });
  }

  /* Rungs 2 and 3 ride the thread rung 1 opened. Ryan's rule for the post-quote ladder was
     "each email replies to our previous email ... they can scroll through and see email 4,
     then 3, then 2, then 1" and it holds just as well here: the address they are being asked
     to confirm is sitting a scroll below. */
  function _thread(r, out) {
    if (r.parent && r.parent.messageId) {
      out.replyToMessage = { ref: r.parent.ref || r.ref, messageId: r.parent.messageId };
    } else {
      r.warnings.push('No earlier email to this customer is on the deal, so this one starts a new thread instead of continuing the first.');
    }
    return out;
  }

  /* ============================================
     1 — the voicemail follow-up
     ============================================
     Ryan's copy, second pass (2026-08-20). What changed from the first draft is worth
     recording, because most of it is him cutting things out:

       • The order's TIME moved into the opening thank-you ("ordering the ECORE flooring
         samples a little bit ago") and left the voicemail sentence entirely.
       • The voicemail sentence is now four words. It no longer re-states what the samples
         were or where they were ordered - the sentence above it just said both.
       • The questions changed to what a flooring buyer actually asks: material, colour
         options, PRODUCT USE CASE, INSTALLATION. "Ordering process" and "difference in
         products" are gone.
       • The phone is its own sentence: "Reach me any time at ...".
       • "the samples", not "some samples" - by now they are a specific known box.

     ⚠️ ONE CLOCK, NOT TWO. The first draft carried the call's time and the order's time in
     one sentence and had to suppress one when they matched. Ryan's rewrite solved that by
     moving them into different sentences: the order gets its phrase, the call gets "just" or
     nothing. Simpler copy AND simpler code, which is usually the tell that the copy is right. */
  function _build1(ctx) {
    return _read(ctx).then(function (r) {
      var k = r.kinds;
      var one = k.total === 1;
      var paras = [B.greeting(r.first)];

      /* ---- the thank-you, which now carries when they ordered ---- */
      var ordered = (r.dq && r.dq.createdAt) ? T.whenPhrase(r.dq.createdAt, r.now) : { phrase: '', just: false };
      paras.push([{ t: 'Thanks very much for ordering ' + _theirOrder(k)
        + (ordered.phrase ? ' ' + ordered.phrase : '') + '.' }]);

      /* ---- the call, then the offer of help, in one paragraph ----
         ⛔ THE VOICEMAIL LINE IS A CLAIM ABOUT SOMETHING THAT HAPPENED, so it is built from
         the call log and never assumed: `callVm` decides voicemail vs call, and a missing
         call is a warning to the rep rather than a sentence nobody checked. */
      var call = T.lastNoAnswerCall(r.deals);
      var cw = call ? T.whenPhrase(call.ts, r.now) : { phrase: '', just: false };
      var vm = call ? String(call.callVm || '') : '';
      if (!call) {
        r.warnings.push(T.lastSpokenCall(r.deals)
          ? 'The most recent call logged here was answered, not missed, so the opening line describes a call that is not in the log. Check it before sending.'
          : 'No unanswered call is logged on this deal, so the opening line has no date behind it.');
      } else if (!vm) {
        r.warnings.push('That call was logged without an answer to the voicemail question, so the email says you gave them a call rather than claiming you left one.');
      }
      var callLine = (vm === 'left')
        ? (cw.just ? 'I just left you a brief voicemail.' : 'I left you a brief voicemail' + (cw.phrase ? ' ' + cw.phrase : '') + '.')
        : (cw.just ? 'I just gave you a call.' : 'I gave you a call' + (cw.phrase ? ' ' + cw.phrase : '') + '.');

      var help = " Should you have any questions about the material, color options, product use case, installation, etc. then please don't hesitate to email or call.";
      if (r.phone) {
        paras.push([{ t: callLine + help + ' Reach me any time at ' + r.phone + '.' }]);
      } else {
        paras.push([{ t: callLine + help }]);
        r.warnings.push('No phone number is set on your rep profile, so the offer of help does not include one.');
      }

      /* ---- the ask ----
         ⚠️ ONE SENTENCE, NOT A BLOCK (Ryan 2026-08-20, second revision). It was a label with
         the address on tight lines beneath it; he asked for it inline: "please just confirm
         you'd like the sample shipped to 1725 Del Mar Ave, Laguna Beach, CA 92651." Reading
         it back, it is the better shape for what it asks - the address is the OBJECT of the
         question, and a block turns a one-line confirmation into a form to inspect.
         📌 The `tight` block facility this used to need stays in the modal and the README.
         It is the right answer for a genuine list of lines and the next template that prints
         one should reach for it; this email simply stopped being that. */
      if (r.address) {
        /* ⛔ THE ADDRESS IS BOLD, AND IT IS ITS OWN RUN FOR THAT REASON (Ryan 2026-09-13).
           The sentence is the ask; the address is the THING BEING CHECKED, and a wall of
           unbroken prose makes a person read it instead of scanning it. `b: true` is the
           run-level bold the modal's two renderers learned on the same day - the Quill
           insert and the sent HTML both honour it, so what the rep sees in the composer is
           what lands in the inbox. ⚠️ The full stop stays OUTSIDE the bold run: bolding the
           punctuation after an address is the tell of a template that pasted a string. */
        paras.push([
          { t: "Secondly, please just confirm you'd like " + (one ? 'the sample' : 'the samples') + ' shipped to ' },
          { t: r.address, b: true },
          { t: '.' },
        ]);
      } else {
        paras.push([{ t: "Secondly, please just confirm the address you'd like " + (one ? 'the sample' : 'the samples') + ' shipped to.' }]);
        r.warnings.push('No shipping address is on this deal, so the email asks for one instead of printing it to confirm.');
      }

      paras.push([{ t: "As soon as you confirm, we'll send you " + (one ? 'the sample' : 'the samples')
        + " via FedEx. We'll also send you the tracking number once " + (one ? "it's" : "they're") + ' shipped out.' }]);
      paras.push([{ t: 'Look forward to hearing from you! Thanks again' + (r.first ? ', ' + r.first : '') + '.' }]);

      return { paragraphs: paras, warnings: r.warnings, subject: _subject(r, k) };
    });
  }

  /* ⚠️ NEITHER RUNG BELOW HONOURS `ctx.automated`, AND THEY DO NOT NEED TO (Ryan 2026-08-20).
     When this family is armed as a sequence, rungs 2 and 3 are rendered at arm time and sent up
     to a fortnight later — so anything in them that is a claim about WHEN would go stale, the way
     the quote ladder's call reference does (see email-tpl-followups.js `_read`). These two print
     no date, no call and no address: rung 2 is four sentences about the order they placed, rung 3
     asks for a yes or a no. There is nothing in either that time can falsify.
     ⛔ Do not add the flag here "for symmetry" — add it the day a sentence in one of them starts
     naming a moment.

     ⚠️ RUNG 3 DOES NAME THE BASKET NOW (2026-09-13), and that is still not a claim about WHEN.
     What they picked is a fact about an order that has already been placed, so a fortnight on
     the shelf cannot make "the rubber paver samples" untrue the way "yesterday" goes wrong.
     The one thing that COULD move it is a rep adding samples to the deal after the rung was
     armed - the same staleness an already-sent rung 1 carries, and `automated` would not fix
     it either, since the render happens before the addition either way. */

  /* ============================================
     2 — still needing them?
     ============================================
     ⚠️ TWO SMALL REPAIRS TO THE PASTED COPY, BOTH FLAGGED TO RYAN RATHER THAN MADE QUIETLY:
       • "if you're still need the samples" is a typo for "if you're still needing", which is
         the phrasing he uses in rung 3.
       • The typo only. ⚠️ THE EM DASH IN THE LAST SENTENCE STAYS. It was changed to a full
         stop once, on the strength of the house no-em-dashes rule, and Ryan corrected that:
         *"I don't want YOU to write them, but when I write them and provide it in copy, it's
         intentional."* The rule governs the sentences we invent, not the ones he hands over.
         ⛔ Do not "fix" it again. */
  function _build2(ctx) {
    return _read(ctx).then(function (r) {
      var one = r.kinds.total === 1;
      var paras = [B.greeting(r.first)];
      paras.push([{ t: "Just following up to see if you're still needing the " + (one ? 'sample' : 'samples') + ' you ordered on our website?' }]);
      paras.push([{ t: 'If you do, then please just confirm your shipping address.' }]);
      paras.push([{ t: "Finally, please let me know if you have any questions — I'm here to help!" }]);
      paras.push([{ t: 'Thanks' + (r.first ? ' ' + r.first : '') + ', chat soon!' }]);
      return _thread(r, { paragraphs: paras, warnings: r.warnings });
    });
  }

  /* ============================================
     3 — the last one
     ============================================
     ⛔ IT GIVES THEM A WAY TO SAY NO, AND THAT IS THE POINT OF IT. Three unanswered emails
     mean one of two things and we cannot tell which, so the last one stops asking for a
     confirmation and asks for an ANSWER, either answer. Ryan's rewrite sharpened the reason
     out loud: *"please let me know so I know how to best serve you"* - the ask is framed as
     service, so a no costs the customer nothing to send, which is what makes it likely enough
     to be worth asking for.

     ⚠️ NO NAME ON THE SIGN-OFF HERE. Rungs 1 and 2 use it; this one ends on a bare
     "Thank you!". Ryan wrote all three, so the difference is his, and a name on the third
     chase can read as pointed in a way it does not on the first. */
  function _build3(ctx) {
    return _read(ctx).then(function (r) {
      var one = r.kinds.total === 1;
      var paras = [B.greeting(r.first)];
      /* ⛔ THE SAME PHRASE RUNG 1 USED, from the same function (Ryan 2026-09-13, "fix rung 3").
         This line said "the ECORE sample you ordered" while rung 1 two emails up said "the
         rubber paver sample" - one thread, one box, two names for it. `_theirOrder` is now
         the only place in the family that names a basket, which is what stops the two
         drifting apart again.
         ⚠️ It costs the no-paver basket one word: this rung used to say "the ECORE samples"
         and now says "the ECORE flooring samples" - which is the phrase Ryan approved for
         rung 1's first line on the same day, so the two agree rather than the copy having
         been widened on its own. */
      paras.push([{ t: 'Last note from me on ' + _theirOrder(r.kinds) + ' you ordered.' }]);
      paras.push([{ t: "If you're still needing " + (one ? 'it' : 'some') + ", please just reply with your shipping address and I'll get "
        + (one ? 'it' : 'them') + ' sent out.' }]);
      paras.push([{ t: 'If your timing or needs have changed, no problem at all. Either way, please let me know so I know how to best serve you.' }]);
      paras.push([{ t: 'Thank you!' }]);
      return _thread(r, { paragraphs: paras, warnings: r.warnings });
    });
  }

  /* ============================================
     The gates
     ============================================
     ⛔ EVERY ONE OF THESE IS A PURE FUNCTION OF `st`. The kernel builds that state once per
     picker open, so ten templates cost one chain load and one ledger read; a gate that
     fetched would put a network round trip behind opening a menu. */
  function _isSampleDeal(st) {
    return (st.deals || []).some(function (d) {
      return d && ((Array.isArray(d.leadSamples) && d.leadSamples.length) || d.isSample);
    });
  }

  function _shipped(st) {
    return (st.deals || []).some(function (d) {
      return d && d.sampleTracking && String(d.sampleTracking.number || '').trim();
    });
  }

  /* ⛔ RUNG 1 OPENS BY DESCRIBING A PHONE CALL, SO IT IS GREY UNTIL ONE IS LOGGED (Ryan
     2026-08-20). "I just left you a brief voicemail" is a claim about something that
     happened; sent off an empty note log it is either a lie or a rep who forgot to log the
     call, and both are worth stopping at the menu rather than warning about after the draft
     is on screen. ⚠️ ANY call counts, which is what Ryan asked for. Whether it was ANSWERED
     is a different question and stays a build-time warning: a connected call means the
     opening line describes something that did not happen, but the rep may still have a
     reason to send it, and that judgement is theirs. */
  function _hasCall(st) {
    return !!(T.lastNoAnswerCall(st.deals) || T.lastSpokenCall(st.deals));
  }

  /* The three share everything except which rung has to have gone first. */
  function _gateFor(id, requires) {
    return function (st) {
      if (!_isSampleDeal(st)) return 'This deal has no sample order on it.';
      if (_shipped(st)) return 'The samples have already shipped, so the follow-up ladder takes it from here.';
      if (!requires && !_hasCall(st)) return 'Log the call first. This email opens by referring to it.';
      if (st.wasSent(id)) return 'Already sent to this customer.';
      if (requires && !st.wasSent(requires)) return 'Send the one above it first.';
      /* ⚠️ A REPLY GREYS THE CHASE, IT DOES NOT HIDE IT. If they wrote back after the last
         one, this template is almost certainly the wrong email - but "almost certainly" is
         the rep's call to overrule, and the reason is printed under the row so they can. */
      if (requires) {
        var sentAt = st.sentAt(requires);
        if (sentAt && st.lastInboundAfter(sentAt)) return 'They replied after the last one, so this chase is probably not the right email.';
      }
      return null;
    };
  }

  T.register({
    id: 'samples-confirm-1',
    icon: '📦',
    /* ⚠️ SHORT ENOUGH NOT TO WRAP. Ryan: "change the first sample order description to be
       shorter so it doesn't wrap." The row is a scan target, not a description - what the
       email actually does now lives in the hint, which the picker shows on hover. */
    label: 'Sample order 1 - confirm address',
    hint: 'After the call they missed. Thanks them, offers help, asks them to confirm the ship-to',
    build: _build1,
    gate: _gateFor('samples-confirm-1', null),
  });

  T.register({
    id: 'samples-confirm-2',
    icon: '📦',
    label: 'Sample order 2 - still need them?',
    hint: 'Second nudge. Shorter, replies into the first',
    build: _build2,
    gate: _gateFor('samples-confirm-2', 'samples-confirm-1'),
  });

  T.register({
    id: 'samples-confirm-3',
    icon: '📦',
    label: 'Sample order 3 - last chance',
    hint: 'Final one. Asks for a yes or a no, and makes no feel fine',
    build: _build3,
    gate: _gateFor('samples-confirm-3', 'samples-confirm-2'),
  });
})();
